import { z } from 'zod';
import { TicketingApiError } from './client.js';
const trancheSchema = z.looseObject({
    id: z.number(),
    maxValueIfPercent: z.number().nullable().optional(),
    plafondSubventionMaximal: z.number().nullable().optional(),
    limiteSuperieure: z.number(),
    value: z.number(),
    type: z.enum(['eur', '%', 'pdv']),
});
const subventionSurCategorieSchema = z.looseObject({
    id: z.number(),
    taxons: z.array(z.string()).optional(),
    subventionCategorieTranches: z.array(trancheSchema).optional(),
    // The API expresses "unlimited" as both `null` and the UNLIMITED_QUANTITY sentinel.
    quantity: z.number().nullable(),
    enabled: z.boolean(),
    dateDebut: z.string().nullable(),
    dateFin: z.string().nullable(),
    multiplierParCoefficientSubvention: z.boolean().optional(),
});
const subventionSimpleSchema = z.looseObject({
    id: z.number(),
    productVariant: z.string(),
    subventionTranches: z.array(trancheSchema).optional(),
    quantity: z.number().nullable(),
    enabled: z.boolean(),
    dateDebut: z.string().nullable(),
    dateFin: z.string().nullable(),
});
const limiteDeQuantiteSchema = z.looseObject({
    id: z.number(),
    taxon: z.string(),
    quantity: z.number().nullable(),
    enabled: z.boolean(),
    dateDebut: z.string().nullable(),
    dateFin: z.string().nullable(),
});
const plafondDeSubventionSchema = z.looseObject({
    id: z.number(),
    montant: z.number(),
    montantParAyantDroit: z.number().nullable(),
    taxons: z.array(z.string()),
});
const UNLIMITED_QUANTITY = 2147483647;
const ROOT_TAXON_CODE = 'MENU_CATEGORY';
const PRODUCT_NAME_CONCURRENCY = 5;
const QUANTITY_LEFT_UNIT_PRICE_CENTS = 5000;
const getCode = (iri) => iri.split('/').pop() ?? iri;
const formatEuros = (cents) => `${cents / 100}€`;
const formatTrancheValue = (tranche) => {
    if (tranche.type === 'eur') {
        return formatEuros(tranche.value);
    }
    // Verified against estimate: the salarié pays this price and the subsidy covers the rest.
    if (tranche.type === 'pdv') {
        return `Prix salarié ${formatEuros(tranche.value)}`;
    }
    const percentStr = `${tranche.value}%`;
    if (tranche.maxValueIfPercent) {
        return `${percentStr} (max ${formatEuros(tranche.maxValueIfPercent)})`;
    }
    return percentStr;
};
const formatTranche = (tranche) => tranche.plafondSubventionMaximal
    ? `${formatTrancheValue(tranche)} (plafond ${formatEuros(tranche.plafondSubventionMaximal)})`
    : formatTrancheValue(tranche);
const formatTaxonName = (code, taxonIndex) => {
    if (code === ROOT_TAXON_CODE)
        return 'Toutes les catégories';
    return taxonIndex.get(code)?.path ?? code;
};
const normalizeQuantity = (quantity) => quantity !== null && quantity < UNLIMITED_QUANTITY ? quantity : null;
export const buildSubsidyRules = (sources, taxonIndex, productNameByVariantCode) => {
    const categoryRules = sources.categories.map((rule) => {
        const codes = (rule.taxons ?? []).map(getCode);
        return {
            id: `category-${rule.id}`,
            kind: 'category',
            target: codes.map((code) => formatTaxonName(code, taxonIndex)).join(', '),
            taxonCodes: codes,
            productVariantCode: null,
            // The billetterie multiplies the amount by the card's indiceDeReference.
            formula: `${(rule.subventionCategorieTranches ?? []).map(formatTranche).join(', ')}${rule.multiplierParCoefficientSubvention ? ' × coefficient' : ''}`,
            quantity: normalizeQuantity(rule.quantity),
            dateDebut: rule.dateDebut,
            dateFin: rule.dateFin,
            enabled: rule.enabled,
        };
    });
    const productRules = sources.products.map((rule) => {
        const variantCode = getCode(rule.productVariant);
        return {
            id: `product-${rule.id}`,
            kind: 'product',
            target: productNameByVariantCode.get(variantCode) ?? variantCode,
            taxonCodes: [],
            productVariantCode: variantCode,
            formula: (rule.subventionTranches ?? []).map(formatTranche).join(', '),
            quantity: normalizeQuantity(rule.quantity),
            dateDebut: rule.dateDebut,
            dateFin: rule.dateFin,
            enabled: rule.enabled,
        };
    });
    const quantityLimitRules = sources.quantityLimits.map((rule) => {
        const quantity = normalizeQuantity(rule.quantity);
        const code = getCode(rule.taxon);
        return {
            id: `quantityLimit-${rule.id}`,
            kind: 'quantityLimit',
            target: formatTaxonName(code, taxonIndex),
            taxonCodes: [code],
            productVariantCode: null,
            formula: quantity === null ? 'Sans limite' : `Maximum ${quantity} articles`,
            quantity,
            dateDebut: rule.dateDebut,
            dateFin: rule.dateFin,
            enabled: rule.enabled,
        };
    });
    const ceilingRules = sources.ceilings.map((rule) => {
        const perAyantDroit = rule.montantParAyantDroit !== null
            ? ` + ${formatEuros(rule.montantParAyantDroit)} par ayant-droit`
            : '';
        const codes = rule.taxons.map(getCode);
        return {
            id: `ceiling-${rule.id}`,
            kind: 'ceiling',
            target: codes.map((code) => formatTaxonName(code, taxonIndex)).join(', '),
            taxonCodes: codes,
            productVariantCode: null,
            formula: `${formatEuros(rule.montant)} par salarié${perAyantDroit}`,
            quantity: null,
            dateDebut: null,
            dateFin: null,
            enabled: true,
        };
    });
    return [...categoryRules, ...productRules, ...quantityLimitRules, ...ceilingRules];
};
const readName = (entity) => entity.translations?.fr_FR?.name ?? Object.values(entity.translations ?? {})[0]?.name ?? null;
// A product rule only carries the variant IRI. The product name is what a CSE recognises
// ("Serre Amazonienne"), the variant name what distinguishes its tickets ("Billet d'entrée").
// Sibling variants share their product: `productNames` fetches each product once.
const fetchProductName = async (client, variantCode, productNames) => {
    try {
        const variant = await client.get(`/admin/product-variants/${encodeURIComponent(variantCode)}`);
        const variantName = readName(variant);
        const productCode = variant.product ? getCode(variant.product) : null;
        let productName = null;
        if (productCode) {
            productName =
                productNames.get(productCode) ??
                    client
                        .get(`/admin/products/${encodeURIComponent(productCode)}`)
                        .then(readName);
            productNames.set(productCode, productName);
        }
        return [await productName, variantName].filter(Boolean).join(' · ') || null;
    }
    catch {
        return null;
    }
};
const fetchProductNames = async (client, variantCodes) => {
    const unique = [...new Set(variantCodes)];
    const names = new Map();
    const productNames = new Map();
    let next = 0;
    const workers = Array.from({ length: Math.min(PRODUCT_NAME_CONCURRENCY, unique.length) }, async () => {
        while (next < unique.length) {
            const code = unique[next++];
            const name = await fetchProductName(client, code, productNames);
            if (name)
                names.set(code, name);
        }
    });
    await Promise.all(workers);
    return names;
};
const fetchProductRules = async (client, cseId) => z
    .array(subventionSimpleSchema)
    .parse(await client.getAll(`/admin/cse/${cseId}/subvention-simple`));
const fetchSubsidyRuleSources = async (client, cseId) => {
    const [rawCategories, products, rawQuantityLimits, rawCeilings] = await Promise.all([
        client.getAll(`/admin/cse/${cseId}/subventions-sur-categories`),
        fetchProductRules(client, cseId),
        client.getAll(`/admin/cse/${cseId}/limite-de-quantite-pour-subvention-simple`),
        client.getAll(`/admin/cse/${cseId}/plafond-de-subvention`),
    ]);
    return {
        categories: z.array(subventionSurCategorieSchema).parse(rawCategories),
        products,
        quantityLimits: z.array(limiteDeQuantiteSchema).parse(rawQuantityLimits),
        ceilings: z.array(plafondDeSubventionSchema).parse(rawCeilings),
    };
};
export const getSubsidyRules = async (client, cseId) => {
    const [sources, taxonIndex] = await Promise.all([
        fetchSubsidyRuleSources(client, cseId),
        client.getTaxonIndex(),
    ]);
    const productNames = await fetchProductNames(client, sources.products.map((rule) => getCode(rule.productVariant)));
    return buildSubsidyRules(sources, taxonIndex, productNames);
};
const subventionSimpleVariantSchema = z.looseObject({
    code: z.string(),
    name: z.string().nullable(),
    productName: z.string().nullable(),
    dateDebut: z.string().nullable(),
    dateFin: z.string().nullable(),
});
// A CSE with two rules on the same variant gets it listed twice.
export const buildSubsidyProducts = (variants) => [...new Map(variants.map((v) => [v.code, v])).values()]
    .map((v) => ({
    variantCode: v.code,
    label: [v.productName, v.name].filter(Boolean).join(' · ') || v.code,
    dateDebut: v.dateDebut,
    dateFin: v.dateFin,
}))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
export const getSubsidyProducts = async (client, cseId) => buildSubsidyProducts(z
    .array(subventionSimpleVariantSchema)
    .parse(await client.getAll(`/admin/cse/${cseId}/product-variants/subvention-simple`)));
// Mirrors the billetterie: a rule set on the taxon or on any ancestor applies, a product rule
// replaces the category rules for its variant, and quantity limits only cap product rules.
export const selectSubsidyRules = (rules, scope) => {
    const onLineage = (rule) => rule.taxonCodes.some((code) => scope.taxonCodes.includes(code));
    const isTargetedProduct = (rule) => rule.kind === 'product' &&
        scope.productVariantCode !== null &&
        rule.productVariantCode === scope.productVariantCode;
    const hasProductRule = rules.some((rule) => isTargetedProduct(rule) && rule.enabled);
    return rules.filter((rule) => {
        switch (rule.kind) {
            case 'product':
                return isTargetedProduct(rule);
            case 'category':
                return !hasProductRule && onLineage(rule);
            case 'quantityLimit':
                return scope.productVariantCode !== null && onLineage(rule);
            case 'ceiling':
                return onLineage(rule);
        }
    });
};
/**
 * The categories an enabled category rule applies to: those it is set on and their descendants,
 * as selectSubsidyRules matches up the lineage. Date windows are ignored: a seasonal rule still
 * subsidises the category during its season.
 */
export const getRuledCategoryIds = (categories, rules) => {
    const ruledCodes = new Set(rules.filter((rule) => rule.kind === 'category' && rule.enabled).flatMap((r) => r.taxonCodes));
    if (ruledCodes.has(ROOT_TAXON_CODE))
        return new Set(categories.map((category) => category.id));
    const byId = new Map(categories.map((category) => [category.id, category]));
    const isRuled = (category) => category !== undefined &&
        (ruledCodes.has(category.code) ||
            (category.parentId !== null && isRuled(byId.get(category.parentId))));
    return new Set(categories.filter(isRuled).map((category) => category.id));
};
const fetchVariantMainTaxonCode = async (client, variantCode) => {
    try {
        const variant = await client.get(`/admin/product-variants/${encodeURIComponent(variantCode)}`);
        if (!variant.product)
            return null;
        const product = await client.get(`/admin/products/${encodeURIComponent(getCode(variant.product))}`);
        return product.mainTaxon ? getCode(product.mainTaxon) : null;
    }
    catch (err) {
        if (err instanceof TicketingApiError && err.status === 404)
            return null;
        throw err;
    }
};
const resolveRuleScope = async (client, target) => {
    if ('categoryId' in target) {
        return {
            taxonCodes: await client.getTaxonLineage({ id: target.categoryId }),
            productVariantCode: null,
        };
    }
    const mainTaxonCode = await fetchVariantMainTaxonCode(client, target.productVariantCode);
    return {
        taxonCodes: mainTaxonCode ? await client.getTaxonLineage({ code: mainTaxonCode }) : [],
        productVariantCode: target.productVariantCode,
    };
};
// Naming a product rule costs two requests: only the rules shown get named.
const nameProductRules = async (client, rules) => {
    const productNames = await fetchProductNames(client, rules.flatMap((rule) => (rule.productVariantCode ? [rule.productVariantCode] : [])));
    return rules.map((rule) => rule.productVariantCode
        ? { ...rule, target: productNames.get(rule.productVariantCode) ?? rule.target }
        : rule);
};
const selectRulesForTargets = async (client, cseId, targets) => {
    const [sources, taxonIndex, scopes] = await Promise.all([
        fetchSubsidyRuleSources(client, cseId),
        client.getTaxonIndex(),
        Promise.all(targets.map((target) => resolveRuleScope(client, target))),
    ]);
    const rules = buildSubsidyRules(sources, taxonIndex, new Map());
    return scopes.map((scope) => ({ scope, applicable: selectSubsidyRules(rules, scope) }));
};
/** The CSE's rules that weigh on a subsidy estimated on this target. */
export const getSubsidyRulesFor = async (client, cseId, target) => {
    const [{ applicable }] = await selectRulesForTargets(client, cseId, [target]);
    return nameProductRules(client, applicable);
};
// Dates come as "2026-01-01" or "2025-06-01 00:00:00": the day is enough.
const isInEffect = (rule, day) => rule.enabled &&
    (!rule.dateDebut || rule.dateDebut.slice(0, 10) <= day) &&
    (!rule.dateFin || rule.dateFin.slice(0, 10) >= day);
/**
 * Among the rules that apply to a target (see selectSubsidyRules), the one its subsidy comes from:
 * the product rule, otherwise the category rule set on the closest taxon. Null when no rule in
 * effect subsidises the target.
 */
export const pickSubsidisingRule = (applicable, scope, at) => {
    const day = at.toISOString().slice(0, 10);
    const inEffect = applicable.filter((rule) => isInEffect(rule, day));
    const productRule = inEffect.find((rule) => rule.kind === 'product');
    if (productRule)
        return productRule;
    const distance = (rule) => Math.min(...rule.taxonCodes.map((code) => scope.taxonCodes.indexOf(code)).filter((index) => index >= 0));
    return inEffect
        .filter((rule) => rule.kind === 'category')
        .reduce((closest, rule) => (!closest || distance(rule) < distance(closest) ? rule : closest), null);
};
export const getSubsidisingRule = async (client, cseId, target, at) => {
    const [{ scope, applicable }] = await selectRulesForTargets(client, cseId, [target]);
    const rule = pickSubsidisingRule(applicable, scope, at);
    if (!rule)
        return null;
    const [named] = await nameProductRules(client, [rule]);
    return named;
};
/**
 * The quotas an article on a target is counted on: the rule its subsidy comes from and, above a
 * product rule, the limites on its taxons. Two targets sharing one compete for the same articles.
 */
export const pickSubsidyQuotaIds = (applicable, scope, at) => {
    const subsidising = pickSubsidisingRule(applicable, scope, at);
    if (!subsidising)
        return [];
    const day = at.toISOString().slice(0, 10);
    const limites = subsidising.kind === 'product'
        ? applicable.filter((rule) => rule.kind === 'quantityLimit' && isInEffect(rule, day))
        : [];
    return [subsidising, ...limites].filter((rule) => rule.quantity !== null).map((rule) => rule.id);
};
export const getSubsidyQuotaIds = async (client, cseId, targets, at) => (await selectRulesForTargets(client, cseId, targets)).map(({ scope, applicable }) => pickSubsidyQuotaIds(applicable, scope, at));
// Each rule family has its own pair of external-order endpoints.
const toTargetRequest = async (client, target) => {
    if ('productVariantCode' in target) {
        return {
            endpoint: 'subvention-simple',
            payload: {
                productVariant: `/api/v2/admin/product-variants/${encodeURIComponent(target.productVariantCode)}`,
            },
        };
    }
    const taxonCode = await client.getTaxonCode(target.categoryId);
    return taxonCode
        ? {
            endpoint: 'subvention-sur-categorie',
            payload: { taxon: `/api/v2/admin/taxons/${taxonCode}` },
        }
        : null;
};
const subventionEstimateSchema = z.looseObject({
    montant: z.string().nullable(),
    maxQuantity: z.number().nullable(),
});
const postSubsidyEstimate = (client, cseId, cardId, { endpoint, payload }, line) => client.post(`/admin/cse/${cseId}/cards/${cardId}/external-order/${endpoint}/estimate`, { ...payload, ...line });
export const getSubsidyFor = async (client, cseId, cardId, target, unitPriceEuros, productName = 'Prestation', quantity = 1) => {
    const targetRequest = await toTargetRequest(client, target);
    if (!targetRequest) {
        return 0;
    }
    const unitPriceCents = Math.round(unitPriceEuros * 100);
    const response = await postSubsidyEstimate(client, cseId, cardId, targetRequest, {
        productName,
        unitPrice: unitPriceCents,
        quantity,
    });
    if (!response.montant) {
        return 0;
    }
    // The billetterie grants a fixed-amount rule in full even above the price of what was bought
    const lineTotalCents = unitPriceCents * quantity;
    return Math.min(Number.parseFloat(response.montant), lineTotalCents) / 100;
};
const hasSubsidiesDisabled = async (client, cseId, cardId) => {
    const card = await client.get(`/admin/cse/${cseId}/cards/${cardId}`);
    return Boolean(card.subventionsDisabledAt);
};
/**
 * The articles this card can still get subsidised on a target a quantity rule applies to, every
 * rule and limite combined, null when nothing caps them. Price and quantity asked about change
 * nothing to it. A 0 can mean a card that gets no subsidy at all rather than a used-up quota: on a
 * category only a used-up quota comes with a "0" montant, but on a product both come with a null
 * one, so there the card is what tells them apart.
 */
export const getSubsidyQuantityLeft = async (client, cseId, cardId, target) => {
    const targetRequest = await toTargetRequest(client, target);
    if (!targetRequest) {
        return null;
    }
    const { montant, maxQuantity } = subventionEstimateSchema.parse(await postSubsidyEstimate(client, cseId, cardId, targetRequest, {
        productName: 'Prestation',
        unitPrice: QUANTITY_LEFT_UNIT_PRICE_CENTS,
        quantity: 1,
    }));
    if (maxQuantity !== 0 || montant !== null)
        return maxQuantity;
    if ('categoryId' in target)
        return null;
    return (await hasSubsidiesDisabled(client, cseId, cardId)) ? null : 0;
};
export const consumeSubsidy = async (client, cseId, cardId, target, unitPriceEuros, externalOrderId, productName = 'Prestation', quantity = 1) => {
    const targetRequest = await toTargetRequest(client, target);
    if (!targetRequest) {
        return 0;
    }
    const unitPriceCents = Math.round(unitPriceEuros * 100);
    const response = await client.post(`/admin/cse/${cseId}/cards/${cardId}/external-order/${targetRequest.endpoint}/consume`, {
        ...targetRequest.payload,
        productName,
        unitPrice: unitPriceCents,
        quantity,
        externalOrderId,
    });
    return (response.montant ?? 0) / 100;
};
